import { useEffect, useRef, useState } from "react";
import { RotateCcw, Pause, Play, ZoomIn, ZoomOut, DoorOpen, Ruler } from "lucide-react";
import { img } from "../lib/api";

const PRESETS = [["front", 0, 0], ["right", 0, -90], ["back", 0, 180], ["left", 0, 90], ["top", -90, 0], ["bottom", 90, 0]];

export default function Product3D({ views, dimensions }) {
  const [rot, setRot] = useState({ x: -12, y: -30 });
  const [auto, setAuto] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [open, setOpen] = useState(false);
  const [anim, setAnim] = useState(false);
  const drag = useRef(null);

  const [auto3, setAuto3] = useState(null);
  useEffect(() => {
    if (dimensions?.h || !views?.front) return;
    const load = (u) => new Promise((ok) => { if (!u) return ok(null); const i = new Image(); i.onload = () => ok(i); i.onerror = () => ok(null); i.src = img(u); });
    Promise.all([load(views.front), load(views.left || views.right), load(views.top)]).then(([f, s, t]) => {
      if (!f) return;
      const h = 100, w = (100 * f.naturalWidth) / f.naturalHeight;
      const d = s ? (100 * s.naturalWidth) / s.naturalHeight : t ? (w * t.naturalHeight) / t.naturalWidth : w * 0.8;
      setAuto3({ h, w: Math.min(Math.max(w, 15), 400), d: Math.min(Math.max(d, 15), 400) });
    });
  }, [views?.front, views?.left, views?.right, views?.top, dimensions?.h]);
  const d0 = dimensions?.h ? { h: +dimensions.h, w: +dimensions.w, d: +dimensions.d } : auto3 || { h: 100, w: 60, d: 50 };
  const box = useRef(null);
  const [side, setSide] = useState(360);
  useEffect(() => { const el = box.current; if (!el) return undefined; const ro = new ResizeObserver(([e]) => setSide(e.contentRect.width)); ro.observe(el); return () => ro.disconnect(); }, []);
  const S = (side * 0.5) / Math.max(d0.h, d0.w, d0.d);
  const H = d0.h * S, W = d0.w * S, D = d0.d * S;

  useEffect(() => {
    if (!auto) return;
    let id;
    const tick = () => { setRot((r) => ({ ...r, y: r.y + 0.3 })); id = requestAnimationFrame(tick); };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [auto]);

  const [active, setActive] = useState("");
  const goTo = (x, y, name = "") => {
    setAuto(false); setActive(name);
    setRot((r) => {
      const cur = ((r.y % 360) + 540) % 360 - 180;
      return { x: r.x, y: cur };
    });
    requestAnimationFrame(() => { setAnim(true); setRot((r) => ({ x, y: r.y + ((((y - r.y) % 360) + 540) % 360 - 180) })); setTimeout(() => setAnim(false), 700); });
  };
  const down = (e) => { setAuto(false); setAnim(false); setActive(""); drag.current = { x: e.clientX, y: e.clientY, r: rot }; e.currentTarget.setPointerCapture(e.pointerId); };
  const move = (e) => {
    const s = drag.current; if (!s) return;
    setRot({ y: s.r.y + (e.clientX - s.x) * 0.5, x: Math.max(-90, Math.min(90, s.r.x - (e.clientY - s.y) * 0.4)) });
  };
  const toggleDoor = () => { if (!open) goTo(-8, -18); setOpen((o) => !o); };

  const face = (w, h, transform, src, extra = {}) => ({
    style: { width: w, height: h, left: (W - w) / 2, top: (H - h) / 2, transform, ...extra },
    className: "absolute overflow-hidden border border-black/20 bg-white [backface-visibility:hidden]",
    src: src ? img(src) : null,
  });
  const faces = [
    ["inside", face(W - 4, H - 4, `translateZ(${D / 2 - 3}px)`, views.inside || views.front)],
    ["back", face(W, H, `rotateY(180deg) translateZ(${D / 2}px)`, views.back)],
    ["right", face(D, H, `rotateY(90deg) translateZ(${W / 2}px)`, views.right)],
    ["left", face(D, H, `rotateY(-90deg) translateZ(${W / 2}px)`, views.left)],
    ["top", face(W, D, `rotateX(90deg) translateZ(${H / 2}px)`, views.top)],
    ["bottom", face(W, D, `rotateX(-90deg) translateZ(${H / 2}px)`, views.bottom, { background: "#2a2c33" })],
  ];
  const btn = "px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-colors";

  return (
    <div ref={box} data-testid="product-3d-box-viewer" className="relative aspect-square rounded-3xl bg-[#12141c] border border-white/10 overflow-hidden select-none hero-radial">
      <div className="absolute inset-0 grid place-items-center cursor-grab active:cursor-grabbing touch-none [perspective:1200px]" onPointerDown={down} onPointerMove={move} onPointerUp={() => (drag.current = null)}>
        <div className="relative [transform-style:preserve-3d]" style={{ width: W, height: H, transform: `scale(${zoom}) rotateX(${rot.x}deg) rotateY(${rot.y}deg)`, transition: anim ? "transform .7s cubic-bezier(.2,.7,.2,1)" : "none" }}>
          {faces.map(([k, f]) => (
            <div key={k} data-testid={`box-face-${k}`} className={f.className} style={f.style}>
              {f.src && <img src={f.src} alt={k} draggable={false} className="w-full h-full object-contain" />}
            </div>
          ))}
          <div data-testid="box-face-front" className="absolute overflow-hidden border border-black/20 bg-[#c9cbd1] [backface-visibility:hidden]"
            style={{ width: W, height: H, transformOrigin: "left center", transform: `translateZ(${D / 2}px) rotateY(${open ? -110 : 0}deg)`, transition: "transform .9s cubic-bezier(.3,.7,.2,1)" }}>
            <img src={img(views.front)} alt="front" draggable={false} className="w-full h-full object-contain" />
          </div>
        </div>
        <div className="absolute bottom-[12%] w-1/2 h-6 rounded-[50%] bg-black/70 blur-xl pointer-events-none" />
      </div>
      <div className="absolute top-4 left-4 right-4 flex flex-wrap items-center gap-1.5 z-[1]">
        {PRESETS.filter(([n]) => n !== "bottom" || views.bottom).map(([n, x, y]) => <button key={n} data-testid={`box-view-${n}`} aria-pressed={active === n} onClick={() => { setOpen(false); goTo(x, y, n); }} className={`${btn} ${active === n ? "bg-white text-black" : "bg-black/60 backdrop-blur hover:bg-white hover:text-black"}`}>{n}</button>)}
        {views.inside && <button data-testid="box-view-inside" onClick={toggleDoor} className={`${btn} flex items-center gap-1 ${open ? "bg-[#ff5500] text-white" : "bg-black/60 backdrop-blur hover:bg-[#ff5500]"}`}><DoorOpen size={12} />{open ? "Close" : "Inside"}</button>}
        {dimensions?.h && <div className="basis-full" />}
        {dimensions?.h && <p data-testid="box-dimensions" className="w-fit pointer-events-none text-[11px] text-[#00f0ff] flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur"><Ruler size={12} />H {dimensions.h} × W {dimensions.w} × D {dimensions.d} cm</p>}
      </div>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/60 backdrop-blur-xl rounded-full p-1.5 border border-white/10">
        <button data-testid="viewer-autorotate-toggle" onClick={() => setAuto((a) => !a)} className="p-2 rounded-full hover:bg-white/10">{auto ? <Pause size={16} /> : <Play size={16} />}</button>
        <button data-testid="viewer-zoom-in" onClick={() => setZoom((z) => Math.min(1.6, z + 0.1))} className="p-2 rounded-full hover:bg-white/10"><ZoomIn size={16} /></button>
        <button data-testid="viewer-zoom-out" onClick={() => setZoom((z) => Math.max(0.6, z - 0.1))} className="p-2 rounded-full hover:bg-white/10"><ZoomOut size={16} /></button>
        <button data-testid="viewer-reset" onClick={() => { setOpen(false); setZoom(1); goTo(-12, -30); }} className="p-2 rounded-full hover:bg-white/10"><RotateCcw size={16} /></button>
      </div>
    </div>
  );
}
